import Vue from 'vue'
import Vuex from 'vuex'
import { getInstance } from '@/auth/auth0-plugin'

import Parties from '@/axios/parties'
import Rules from '@/axios/rules-management'

Vue.use(Vuex)

export const retryApiCall = async (apiCall, maxRetries = 3, retryDelay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      
      const shouldRetry = (
        error.code === 'ECONNABORTED' ||
        error.code === 'TIMEOUT' ||
        error.message?.includes('timeout') ||
        error.message?.includes('Network Error') ||
        (error.response?.status >= 500 && error.response?.status < 600)
      );
      
      if (!shouldRetry || attempt === maxRetries) {
        break;
      }
      
      const delay = retryDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

export default new Vuex.Store({
  state: {
    userRelationships: [],
    adGroups: [],
    snackbar: {},
    userProfileTprStoreGroups: [],
    viewMode: 'AD_GROUP'
  },
  getters: {
    adGroups(state) {
      return state.adGroups
    },
    userRelationships(state) {
      return state.userRelationships
    },
    tprProStores(state) {
      const isTprProStore = s => {
        const flag = s?.is_price_pro_store
        return typeof flag === 'string' ? flag.toLowerCase() === 'true' : Boolean(flag)
      }
      return state.userProfileTprStoreGroups.filter(s => s?.party_type === 'STORE' && isTprProStore(s))
    },
    userProfileTprStoreGroups(state) {
      return state.userProfileTprStoreGroups
    },
    viewMode(state) {
      return state.viewMode
    },
    email () {
      const authService = getInstance()
      if (authService.isAuthenticated) {
        return authService.user.email
      }
      return ''
    }
  },
  mutations: {
    setSnackbar(state, payload) {
      state.snackbar = payload
    },
    setAdGroups(state, payload) {
      state.adGroups = payload
    },
    setUserRelationships(state, payload) {
      state.userRelationships = payload
    },
    setUserProfileTprStoreGroups(state, payload) {
      state.userProfileTprStoreGroups = payload
    },
    setViewMode(state, payload) {
      const value = payload === 'STORE' ? 'STORE' : 'AD_GROUP'
      state.viewMode = value
      try {
        sessionStorage.setItem('tpr_manager_view_mode', value)
      } catch (e) {
        console.warn('Unable to persist view mode to sessionStorage', e)
      }
    }
  },
  actions: {
    setSnackbar({ commit }, snackbar) {
      snackbar.showing = true
      snackbar.timeout = 3000
      if (snackbar.status === 'error') {
        snackbar.timeout = -1
      }
      snackbar.icon =
        snackbar.status === 'success'
          ? 'mdi-checkbox-marked-circle-outline'
          : 'mdi-alert'
      snackbar.closeIcon = 'mdi-close-thick'
      snackbar.color = snackbar.status === 'success' ? 'success' : 'alert'

      if (snackbar.text.toLowerCase().includes("network error")) {
        const regex = /network error.*/i      // Remove 'Network Error' from message. Replace with 'Please refresh the page'
        snackbar.text = snackbar.text.replace(regex, "Please refresh the page.")
        console.error("Unable to connect to backend. Page refresh may be needed.")
      }
      commit('setSnackbar', snackbar)
    },
    initViewMode({ commit }) {
      let stored = null
      try {
        stored = sessionStorage.getItem('tpr_manager_view_mode')
      } catch (e) {
        stored = null
      }
      const value = stored === 'STORE' ? 'STORE' : 'AD_GROUP'
      commit('setViewMode', value)
    },
    async getAdGroups(context) {
      // const authService = getInstance()
      if (context.getters.adGroups.length > 0) return
      try {
        const res = await Parties.getAdGroups()
        if (res?.data.length) {
          const adGroups = res.data.filter(result => {
            if (result?.attributes?.AD_GROUP_CODE) {
              return result
            }
          })
          context.commit('setAdGroups', adGroups)
        }
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        context.dispatch('setSnackbar', {
          status: 'error',
          text: `Error retrieving ad groups: ${message}`
        })
      }
    },
    async getUserRelationships(context) {
      const authService = getInstance()
      const { email } = authService.user
      try {
        const relationships = await Parties.getUserProfile(email)
        context.commit(
          'setUserRelationships',
          relationships.data.filter(
            relationship =>
              relationship.party_type_constant === 'AD_GROUP' ||
              relationship.party_type_constant === 'STORE'
          )
        )
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        context.dispatch('setSnackbar', {
          status: 'error',
          text: `Error retrieving user information: ${message}`
        })
      }
    },
    async getUserTprProStores(context, { force = false } = {}) {
      const authService = getInstance()
      if (authService?.tenant !== 'alliance-retail-group') return []
      try {
        await context.dispatch('getUserProfileTprStoreGroups', { force })
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        context.dispatch('setSnackbar', {
          status: 'error',
          text: `Error retrieving stores: ${message}`
        })
      }
      return context.getters.tprProStores
    },
    async getUserProfileTprStoreGroups(context, { force = false } = {}) {
      if (!force && context.state.userProfileTprStoreGroups.length > 0) {
        return context.state.userProfileTprStoreGroups
      }

      const authService = getInstance()
      const email = authService?.user?.email
      const tenant = authService?.tenant

      if (!email) {
        context.commit('setUserProfileTprStoreGroups', [])
        return []
      }

      const { data } = await Rules.getStores(email, tenant)
      const groups = Array.isArray(data) ? data : []
      context.commit('setUserProfileTprStoreGroups', groups)
      return groups
    }
  },
  modules: {}
})
